import React, { useState } from 'react';
import { Button, Empty, FormTitle, Radio, Tooltip, useBreadcrumbRoutes, Wrapper } from '@maxtropy/components';
import styles from './index.module.scss';
import { Mode, ModeDisplay, VerticalStationColor, VerticalStationLegend } from './const';
import { RadioChangeEvent } from 'antd/es/radio';
import { Spin, Tooltip as AntdTooltip } from 'antd';
import EachStation from './components/EachStation';
import EachStationVertical from './components/EachStationVertical';
import { useRequest } from 'ahooks';
import {
  apiV2PowerRoomConfigPowerSystemConfigurationTreeDataPost,
  apiV2PowerRoomConfigPowerSystemConfigurationTreePost,
  V2PowerRoomConfigPowerSystemConfigurationTreeDataPostResponse,
  V2PowerRoomConfigPowerSystemConfigurationTreePostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { getTreeCircuitIds, getTreeMonitorCircuitIds, mergeCircuitDataToItemList } from './utils';
import DetailDrawer from './components/DetailDrawer';
import classNames from 'classnames';
import { customerLayoutMock } from './customerLayoutMock';

// 现场复杂拓扑布局验证完成后改为 false，即恢复正式接口数据。
const USE_CUSTOMER_LAYOUT_MOCK = false;

const PowerSystemConfiguration = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [modeValue, setModeValue] = useState<Mode>(Mode.V);
  const [visible, setVisible] = useState<boolean>(false);
  const [recordData, setRecordData] = useState<any>();
  const [activeIndex, setActiveIndex] = useState<string>();

  const onRadioChange = (e: RadioChangeEvent) => {
    setModeValue(e.target.value);
  };

  const { data, loading } = useRequest(async () => {
    if (USE_CUSTOMER_LAYOUT_MOCK) {
      return customerLayoutMock;
    }

    const res = await apiV2PowerRoomConfigPowerSystemConfigurationTreePost({});
    // 过滤掉 powerSystemBusPaiList 为 null 或空数组的项
    if (res.powerSystemList) {
      res.powerSystemList = res.powerSystemList.filter(
        (item: any) => item.powerSystemBusPaiList && item.powerSystemBusPaiList.length > 0
      );
    }
    const idList = getTreeCircuitIds(res);
    const monitorIdList = getTreeMonitorCircuitIds(res);
    let idListRes: V2PowerRoomConfigPowerSystemConfigurationTreeDataPostResponse | undefined;
    let monitorIdListRes: V2PowerRoomConfigPowerSystemConfigurationTreeDataPostResponse | undefined;
    if (idList?.length) {
      idListRes = await apiV2PowerRoomConfigPowerSystemConfigurationTreeDataPost({ ids: idList });
    }
    if (monitorIdList?.length) {
      monitorIdListRes = await apiV2PowerRoomConfigPowerSystemConfigurationTreeDataPost({ ids: monitorIdList });
    }
    const finalData = mergeCircuitDataToItemList(idListRes?.list ?? [], monitorIdListRes?.list ?? [], res);
    return finalData as V2PowerRoomConfigPowerSystemConfigurationTreePostResponse & { powerRoomOfFreeList?: any[] };
  });

  const powerSystemList = (data as any)?.powerSystemList ?? [];
  const powerRoomOfFreeList = (data as any)?.powerRoomOfFreeList ?? [];
  const isEmpty = !data || (powerSystemList.length === 0 && powerRoomOfFreeList.length === 0);

  return (
    <div className={styles.scaleContainer}>
      <Wrapper routes={[...(breadcrumbRoutes?.routes ?? [])]}>
        <FormTitle
          title="电力系统组态"
          extraContent={
            <div className={styles.extraContent}>
              <Radio.Group onChange={onRadioChange} value={modeValue} buttonStyle="solid">
                <Radio.Button value={Mode.V}>{ModeDisplay[Mode.V]}</Radio.Button>
                <Radio.Button value={Mode.H}>{ModeDisplay[Mode.H]}</Radio.Button>
              </Radio.Group>
              <Button type="primary" onClick={() => window.open(`/power/system/configuration/list`)}>
                配置
              </Button>
            </div>
          }
        />
        <Spin spinning={loading}>
          <div className={classNames(styles.wrapper, modeValue === Mode.V && styles.wrapper_vertical)}>
            {loading ? (
              <Empty description="数据正在加载" />
            ) : isEmpty ? (
              <Empty description="暂无数据" />
            ) : (
              <>
                {modeValue === Mode.V && (
                  <div className={styles.stationLegend}>
                    <span className={styles.stationLegendTitle}>站房图例</span>
                    {VerticalStationLegend.map(legend => (
                      <AntdTooltip title={legend.level === 1 ? undefined : legend.description} key={legend.level}>
                        <div className={styles.stationLegendItem}>
                          <span
                            className={styles.stationLegendColor}
                            style={{ background: VerticalStationColor[legend.level] }}
                          />
                          <span>{legend.label}</span>
                        </div>
                      </AntdTooltip>
                    ))}
                  </div>
                )}
                <div className={classNames(styles.systemList, modeValue === Mode.V && styles.systemList_vertical)}>
                  {powerSystemList.map((item: any, index: number, arr: any[]) => (
                    <div
                      key={item.circuitId + '-' + index}
                      className={classNames(styles.each_wrapper, modeValue === Mode.V && styles.each_wrapper_vertical)}
                    >
                      {modeValue === Mode.H ? (
                        <EachStation
                          item={item}
                          data={data}
                          getItemInfo={(v, r) => {
                            setVisible(v);
                            setRecordData(r);
                          }}
                          getSelectIndex={val => setActiveIndex(val)}
                          activeIndex={activeIndex}
                        />
                      ) : (
                        <EachStationVertical
                          item={item}
                          data={data}
                          getItemInfo={(v, r) => {
                            setVisible(v);
                            setRecordData(r);
                          }}
                          getSelectIndex={val => setActiveIndex(val)}
                          activeIndex={activeIndex}
                        />
                      )}
                      {modeValue === Mode.V && index < arr.length - 1 && (
                        <div className={classNames(styles.dashLine, styles.dashLine_vertical)} />
                      )}
                    </div>
                  ))}
                </div>
                {modeValue === Mode.V && powerSystemList.length > 0 && <div className={styles.dashLine_vertical} />}
                <div
                  className={styles.freeNodeWrapper}
                  style={{ marginLeft: modeValue === Mode.H && powerSystemList.length ? 180 : 0 }}
                >
                  {powerRoomOfFreeList.map((item: any, index: number) => (
                    <div className={styles.free_item} key={item.roomId + '-' + index}>
                      <Tooltip title={item.roomName}>
                        <span className={styles.name}>{item.roomName}</span>
                      </Tooltip>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </Spin>
        <DetailDrawer
          visible={visible}
          recordData={recordData}
          onClose={() => {
            setVisible(false);
            setRecordData(undefined);
            setActiveIndex(undefined);
          }}
        />
      </Wrapper>
    </div>
  );
};

export default PowerSystemConfiguration;
